import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "../controls/Icon";
import useNavbar from "../../common/hooks/NavbarHooks";
import Nav from 'react-bootstrap/Nav';
import { useHasAnyPermission } from "../../common/hooks/Hooks";
import UI_PERMISSIONS_NAME from "../../config/uiPermissionsNames";
import CONFIG from "../../config/config";
import { useAppContext } from "../../common/ApplicationServicesContext";

const HorizontalNavbar = () => {
    const { objectSubType } = useParams();
    const { pathname } = useLocation();
    const objectType = pathname.split("/")[1] || undefined;
    const [closingItemId, setClosingItemId] = useState(null);
    const closingTimeoutRef = useRef(null);
    const [translate] = useTranslation();
    const {
        handleDomainManagementClick,
        handleDeploymentClick,
        handleDrillDownClick,
        handleNestedDrillDownClick,
        selectedTab,
        deployments,
        drillDownObjects,
        next,
        previous,
        hasNextPage,
        currentPage,
        isUnlocked,
        handleUnlockClick,
        confirmationPrompt,
        unlockTimerToast,
        deploymentEditPermission,
    } = useNavbar();
    const { isRootDomain } = useAppContext();

    const viewDomainManagementPermission = useHasAnyPermission({
        names: [
            UI_PERMISSIONS_NAME.ViewDomain,
            UI_PERMISSIONS_NAME.EditDomain,
            UI_PERMISSIONS_NAME.ViewUsers,
            UI_PERMISSIONS_NAME.EditUsers,
            UI_PERMISSIONS_NAME.ViewEnvironments,
            UI_PERMISSIONS_NAME.EditEnvironments
        ]
    });

    const prevObjectTypeRef = useRef(objectType);

    useEffect(() => {
        const prevObjectType = prevObjectTypeRef.current;

        if (prevObjectType && prevObjectType !== objectType) {
            setClosingItemId(prevObjectType);

            closingTimeoutRef.current = setTimeout(() => {
                setClosingItemId(null);
            }, 400);
        }

        prevObjectTypeRef.current = objectType;

        return () => {
            if (closingTimeoutRef.current) {
                clearTimeout(closingTimeoutRef.current);
            }
        };
    }, [objectType]);

    const handleSelect = useCallback((eventId) => {
        switch (eventId) {
            case 'domain-management':
                return handleDomainManagementClick();
            case 'previous-deployment':
                return previous();
            case 'next-deployment':
                return next();
            default: {
                const deployment = deployments.find(({ _id }) => _id === eventId);
                if (deployment) {
                    return handleDeploymentClick(deployment);
                }
                return;
            }
        }
    }, [handleDomainManagementClick, previous, next, deployments, handleDeploymentClick]);

    const header = useMemo(() => {
        const activeKey = typeof selectedTab === 'string' ? selectedTab : selectedTab?._id;

        return <Nav variant='tabs' className='px-3 pt-2' activeKey={activeKey} onSelect={handleSelect}>
            {viewDomainManagementPermission && <Nav.Item>
                <Nav.Link eventKey='domain-management'>
                    {translate('OTM_DOMAIN_MANAGEMENT')}
                </Nav.Link>
            </Nav.Item>}
            {currentPage !== 1 && <Nav.Item>
                <Nav.Link className='px-0' eventKey='previous-deployment'>
                    <Icon icon="ChevronLeft" className="d-block" />
                </Nav.Link>
            </Nav.Item>}
            {deployments.map((deployment) => (
                <Nav.Item key={deployment._id}>
                    <Nav.Link eventKey={deployment._id} className="d-flex align-items-center">
                        {CONFIG.LOCKED_STAGES.includes(deployment.environment?.stage) &&
                            <Icon
                                icon={isUnlocked(deployment._id) ? "LockOpen" : "LockOutlined"}
                                className={
                                    [
                                        "lockIcon fs-5 me-1",
                                        activeKey === deployment._id && deploymentEditPermission[deployment._id] ? "highlight" : "",
                                        isUnlocked(deployment._id) ? "text-success" : " text-danger",
                                    ].filter(Boolean).join(" ")
                                }
                                onClick={(e) => {
                                    if (activeKey === deployment._id && deploymentEditPermission[deployment._id]) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleUnlockClick(deployment);
                                    }
                                }}
                            />
                        }
                        {deployment.name}
                    </Nav.Link>
                </Nav.Item>
            ))}
            {hasNextPage && <Nav.Item>
                <Nav.Link className='px-0' eventKey='next-deployment'>
                    <Icon icon="ChevronRight" className="d-block" />
                </Nav.Link>
            </Nav.Item>}
        </Nav>
    }, [selectedTab, handleSelect, translate, deployments, hasNextPage, viewDomainManagementPermission, currentPage, isUnlocked, handleUnlockClick, deploymentEditPermission]);

    const renderNestedDrillDownObject = useCallback((parentEl, element, index) => {
        return element.visible &&
            <li className="nav-item" key={`nested-${index}`}>
                <a href="/"
                    className={`nav-link ${objectSubType === element.id ? "active" : ""}`}
                    onClick={(event) => { event.preventDefault(); handleNestedDrillDownClick(parentEl.id, selectedTab._id, element.urlPath); }}
                >
                    {element.name}
                </a>
            </li>
    }, [selectedTab, objectSubType, handleNestedDrillDownClick]);

    const renderDrillDownObject = useCallback((element, index) => {
        const isOpen = objectType === element.id;
        const isClosing = closingItemId === element.id;
        const showNested = element.nestedViews && (isOpen || isClosing);

        return element.visible &&
            <li className="nav-item" key={index}>
                <a href="/"
                    className={`nav-link ${isOpen ? "active" : ""}`}
                    onClick={(event) => { event.preventDefault(); handleDrillDownClick(element.id, selectedTab._id, element.nestedViews); }}
                >
                    {element.name}
                </a>
                {showNested && <ul className={`nestedNavs ${isClosing ? "closing" : ""}`}>
                    {element.nestedViews.map((nestedEl, nestedIndex) => renderNestedDrillDownObject(element, nestedEl, nestedIndex))}
                </ul>}
            </li>
    }, [selectedTab, objectType, closingItemId, handleDrillDownClick, renderNestedDrillDownObject]);

    const drillDownHeader = useMemo(() => {
        if (!selectedTab) {
            return <></>
        }
        return (
            <ul className="nav second-level-nav px-3 bg-white">
                {drillDownObjects.map(renderDrillDownObject)}
            </ul>
        );
    }, [selectedTab, drillDownObjects, renderDrillDownObject]);

    return <>
        {confirmationPrompt}
        {unlockTimerToast}

        <div className='d-none d-lg-block'>
            {!isRootDomain && header}
            {drillDownHeader}
        </div>
    </>
};

export default HorizontalNavbar;